/* eslint-disable react/jsx-max-props-per-line */
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@amzn/stencil-react-components/button';
import { H1 } from '@amzn/stencil-react-components/text';
import { Flex, Spacer } from '@amzn/stencil-react-components/layout';
import { TabBar, TabPanel, TabSwitcher, useTabs } from '@amzn/stencil-react-components/tabs';
import { WA_WAREHOUSE_ALLOW_LIST } from 'src/constants/featureGates';

import { useGlobalContext, useUserDataContext } from '../../hooks';
import {
  isIdleTimeReportingEnabledForUser,
  isReportingEnabledForUser,
} from '../../util/allow_lists';
import { metricSender } from '../../util/metric_sender';
import { ErrorBoundary } from '../ErrorBoundary';
import { HourlyProductivityTab } from '../HourlyProductivityTab';
import { ProductivityIdleTimeReport } from '../ProductivityIdleTimeReport';
import { ProductivityLookbackReport } from '../ProductivityLookbackReport';
import { FeedbackLoader } from '../shared/FeedbackLoader';
import { SPPRSummaryTab } from '../SPPRSummaryTab';

// eslint-disable-next-line no-empty-pattern
export const ProductivitySummary = ({}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { areMetricsSendable, spprSummaries, sqprSummaries } = useUserDataContext();
  const { userDetails, isAdminViewEnabled } = useGlobalContext();

  useEffect(() => {
    if (areMetricsSendable) {
      metricSender.generateCompetenciesMetrics(
        userDetails,
        spprSummaries,
        sqprSummaries,
        'Productivity',
      );
    }
  }, [areMetricsSendable]);

  // eslint-disable-next-line prefer-const
  let tabs = [];
  tabs.push({ label: t('hourly'), value: 'tab-hourly' });
  tabs.push({ label: t('weeklyProductivityReviewSummary'), value: 'tab-sppr' });
  tabs.push({ label: t('feedbacks'), value: 'tab-feedbacks' });
  if (isReportingEnabledForUser(userDetails, isAdminViewEnabled)) {
    if (WA_WAREHOUSE_ALLOW_LIST.includes(userDetails.warehouseId)) {
      tabs.push({ label: '26-Week Productivity Summary', value: 'tab-productivity-report' });
    } else {
      tabs.push({ label: '14-Week Productivity Summary', value: 'tab-productivity-report' });
    }
  }
  if (isIdleTimeReportingEnabledForUser(userDetails, isAdminViewEnabled)) {
    if (WA_WAREHOUSE_ALLOW_LIST.includes(userDetails.warehouseId)) {
      tabs.push({ label: '180-Day Idle Time Summary', value: 'tab-productivity-idleTime-report' });
    } else {
      tabs.push({ label: '90-Day Idle Time Summary', value: 'tab-productivity-idleTime-report' });
    }
  }

  // TODO: Only add tab if permissions are present
  const { tabBarProps, tabSwitcherProps } = useTabs({ tabs });

  return (
    <>
      <Flex flexDirection="row" justifyContent="space-between">
        <H1>{t('contributeTeam')}</H1>
        <Button
          data-cy="home-btn"
          onClick={() => {
            navigate('/');
          }}
        >
          {t('home')}
        </Button>
      </Flex>

      <Spacer height={16} />
      <TabBar {...tabBarProps} />
      <Spacer height={32} />

      <TabSwitcher {...tabSwitcherProps}>
        <TabPanel value="tab-hourly">
          <ErrorBoundary>
            <HourlyProductivityTab />
          </ErrorBoundary>
        </TabPanel>
        <TabPanel value="tab-sppr">
          <ErrorBoundary>
            <SPPRSummaryTab />
          </ErrorBoundary>
        </TabPanel>
        <TabPanel value="tab-feedbacks">
          <ErrorBoundary>
            <FeedbackLoader
              feedbackCategories={['Productivity']}
              subtitle={`${t('productivityFeedbacksSubtitle')} ${t('yesterdaysDataTooltip')}`}
            />
          </ErrorBoundary>
        </TabPanel>
        <TabPanel value="tab-productivity-report">
          <ErrorBoundary>
            <ProductivityLookbackReport />
          </ErrorBoundary>
        </TabPanel>
        <TabPanel value="tab-productivity-idleTime-report">
          <ErrorBoundary>
            <ProductivityIdleTimeReport />
          </ErrorBoundary>
        </TabPanel>
      </TabSwitcher>
    </>
  );
};
